/**
 * Kontrakt for én act (PERFORMANCE.md, fane 7).
 *
 * ---------------------------------------------------------------------------------------------
 * TEN NAMED FIELDS AND A LIST OF EXTRA ONES — sak 64.2.
 *
 * «Feltene skal være konfigurerbare, ikke hardkodet», and the spec names ten of them. One sentence
 * decides how both are true at once: «gjestelisteplasser herfra styrer sperren i People». A term
 * the PLATFORM reads cannot be a row in a free-form list, because the guest list would then have
 * to find «the field somebody marked as the seat count» — a configuration to get wrong, in the one
 * place where getting it wrong lets somebody onto a door list.
 *
 * So the ten are fields, and `terms` is whatever else a house needs. Nothing in the code reads a
 * `terms` row, which is exactly what makes the arrangement safe.
 *
 * ---------------------------------------------------------------------------------------------
 * THE WHOLE TAB IS ONE ANSWER, AND IT IS NEVER SHARED.
 *
 * «Hele fanen krever egen rettighet og deles aldri med eksterne» — `contract:read` is `no` for
 * `external` in the matrix, so there is no grant to express and no narrowing to write. The tab is
 * simply absent for them, which `PERFORMANCE_TABS` already arranges.
 *
 * ---------------------------------------------------------------------------------------------
 * A DOCUMENT IS A VERSION, NOT A FILE. The bytes are an attachment like any other; a version adds
 * the number and whether it is the one in force. «Hvilken som er gjeldende» has exactly one
 * answer, and the database enforces it — the alternative, «highest number wins», is wrong the
 * first time somebody uploads a draft of the next one.
 */
import { z } from 'zod';

import {
  exclusivityModeSchema,
  MAX_CONTRACT_TERMS_TEXT,
  MAX_GUEST_LIST_SEATS,
  MAX_TERM_TEXT,
  provisionPartySchema,
  provisionTermSchema,
} from './booking-terms.js';
import { wireInstant } from './wire-time.js';

export const MAX_CONTRACT_TERMS = 40;
export const MAX_CONTRACT_TEXT = 500;
export const MAX_CONTRACT_NOTE = 4000;
/**
 * Taket `ck_performance_contracts__seats` håndhever.
 *
 * ÉN VERDI, DEFINERT I `booking-terms.ts`. Budet må ha nøyaktig det samme taket: et bud med 600
 * plasser ville gått fint helt til overføringen kopierte det over hit, og da kastet MIDT i løkka —
 * etter at halve programmet var laget. Konstanten bor der framfor her fordi den andre retningen
 * ville gitt to moduler som importerer hverandre.
 */
export const MAX_CONTRACT_SEATS = MAX_GUEST_LIST_SEATS;

/** Fakturastatus. Five values, because «forfalt» and «omtvistet» are different problems. */
export const INVOICE_STATUSES = ['not_sent', 'sent', 'paid', 'overdue', 'disputed'] as const;
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

const shortText = z.string().trim().min(1).max(MAX_CONTRACT_TEXT);
const longText = z.string().trim().min(1).max(MAX_CONTRACT_NOTE);

/** Three uppercase letters, checked here and by `ck_performance_contracts__currency`. */
export const currencyCodeSchema = z
  .string()
  .trim()
  .length(3)
  .regex(/^[A-Z]{3}$/);

export const contractTermDtoSchema = z
  .object({
    id: z.uuid(),
    label: z.string(),
    value: z.string().nullable(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'ContractTerm' });
export type ContractTermDto = z.infer<typeof contractTermDtoSchema>;

export const contractDocumentDtoSchema = z
  .object({
    id: z.uuid(),
    attachmentId: z.uuid(),
    versionNumber: z.number().int(),
    isCurrent: z.boolean(),
    note: z.string().nullable(),
    signedOn: z.iso.date().nullable(),
    /**
     * «ERSTATTET AV VERSJON N» (215.19). Satt når en revisjon tok over: raden kan leses, ikke endres
     * eller slettes. Lagret, fordi det ikke kan utledes etter to revisjoner.
     */
    supersededByVersion: z.number().int().nullable(),
    /** Off the attachment, so the list reads as a list of documents rather than of ids. */
    filename: z.string(),
    byteSize: z.number().int(),
    uploadedAt: wireInstant,
    uploadedByName: z.string().nullable(),
    /** False while the scan is pending or the file is infected — the same gate every file has. */
    downloadable: z.boolean(),
  })
  .meta({ id: 'ContractDocument' });
export type ContractDocumentDto = z.infer<typeof contractDocumentDtoSchema>;

/**
 * The money rolled up from `cost_lines`, which own it.
 *
 * «Kostnadslinjer med konto, budsjett og faktisk, som ruller opp i festivalens Economy-side.» The
 * lines are Economy's rows, filtered to this booking — principle 1 again: this tab SHOWS them and
 * the Economy page owns them, so there is no second table and no second way to enter one.
 */
/**
 * HVOR HONORARET STÅR I REGNSKAPET — fire tilstander, avgjort på serveren (vedtak 85).
 *
 * Klienten skal ikke regne dette ut av status, honorar og kategorier hver for seg; da ville tre
 * sider hatt hver sin mening om hva «ikke ført» betyr. Ordene:
 *
 * - `posted`      — linja står på bookingen, og Økonomi teller den.
 * - `not_signed`  — avtalen er ikke signert ennå. Ingenting er galt; booking planlegger fortsatt.
 * - `no_fee`      — signert, men ingen har skrevet et honorar på kontrakten.
 * - `no_category` — signert med et honorar, men huset har ikke pekt ut hvilken konto honorarer
 *                    føres i. Krever en handling, og kontraktfanen sier hvilken.
 * - `not_posted`  — alt er på plass, men linja står ikke der. To ekte veier hit: en booking som
 *                    ble signert før vedtak 85 fantes, og en linje noen har slettet for hånd.
 *                    Knappen på kontraktfanen fører den.
 */
export const ARTIST_FEE_STATES = [
  'posted',
  'not_signed',
  'no_fee',
  'no_category',
  'not_posted',
] as const;
export const artistFeeStateSchema = z.enum(ARTIST_FEE_STATES).meta({ id: 'ArtistFeeState' });
export type ArtistFeeState = (typeof ARTIST_FEE_STATES)[number];

export const contractEconomySchema = z
  .object({
    budgetMinor: z.number().int(),
    actualMinor: z.number().int(),
    currency: z.string().nullable(),
    lineCount: z.number().int(),
    /** Se `ARTIST_FEE_STATES`. */
    artistFee: artistFeeStateSchema,
  })
  .meta({ id: 'ContractEconomy' });
export type ContractEconomy = z.infer<typeof contractEconomySchema>;

/**
 * AVTALEN — den delen av kontrakten som kom fra budet.
 *
 * ---------------------------------------------------------------------------------------------
 * Atle, 4. september: «Disse feltene må også da følges i performance på artistens kontrakt.»
 *
 * De ti navngitte feltene over er PERFORMANCE.md sine, og de blir stående. Dette er de samme
 * punktene som budskjemaet har, med de samme navnene, fordi overføringen fra bud til kontrakt skal
 * være en kopi og ikke en oversettelse.
 *
 * EN EGEN BOLK OG IKKE TJUE FLATE FELTER TIL: den har en overskrift på skjermen («Avtalen»), den
 * fylles av én kilde, og `sourceBidId` sier hvilket bud det var. Å blande dem inn blant de ti
 * ville skjult at de kom fra et sted.
 *
 * ALLTID TIL STEDE, ALDRI NULL. Kolonnene har defaults — «ikke avtalt», «ingen eksklusivitet», ett
 * sett — så en kontrakt ingen har rørt svarer med et fullt sett tomme punkter. Det er riktig: alle
 * seks ytelsene GJELDER for enhver konsert, spørsmålet er bare om noen har bestemt hvem som tar
 * dem.
 */
export const contractDealSchema = z
  .object({
    setCount: z.number().int().positive(),
    capacity: z.number().int().nullable(),

    accommodation: provisionTermSchema,
    catering: provisionTermSchema,
    travel: provisionTermSchema,
    localTransport: provisionTermSchema,
    backline: provisionTermSchema,
    production: provisionTermSchema,

    exclusivityMode: exclusivityModeSchema,
    exclusivityText: z.string().nullable(),

    announcementText: z.string().nullable(),
    attachmentsText: z.string().nullable(),
    deviationsText: z.string().nullable(),
    /** Vilkårspunktet nederst, med artist og arrangør allerede satt inn av budet. */
    contractTermsText: z.string().nullable(),

    /** Budet vilkårene kom fra, eller null når noen har ført dem inn for hånd. */
    sourceBidId: z.uuid().nullable(),
  })
  .meta({ id: 'ContractDeal' });
export type ContractDeal = z.infer<typeof contractDealSchema>;

const dealText = z.string().trim().min(1).max(MAX_TERM_TEXT);
const dealProvision = z
  .object({ party: provisionPartySchema, note: dealText.nullable() })
  .optional();

/** Avtalen slik kontraktfanen redigerer den. Ytelsene og tekstene, aldri `sourceBidId`. */
export const updateContractDealSchema = z
  .object({
    setCount: z.number().int().min(1).max(10).optional(),
    capacity: z.number().int().min(0).max(1_000_000).nullish(),

    accommodation: dealProvision,
    catering: dealProvision,
    travel: dealProvision,
    localTransport: dealProvision,
    backline: dealProvision,
    production: dealProvision,

    exclusivityMode: exclusivityModeSchema.optional(),
    exclusivityText: dealText.nullish(),

    announcementText: dealText.nullish(),
    attachmentsText: dealText.nullish(),
    deviationsText: dealText.nullish(),
    contractTermsText: z.string().trim().min(1).max(MAX_CONTRACT_TERMS_TEXT).nullish(),
  })
  .meta({ id: 'UpdateContractDeal' });
export type UpdateContractDeal = z.infer<typeof updateContractDealSchema>;

/**
 * DEN DELTE AVTALEN BAK PROGRAMPOSTEN (A8, 279.3 a). Når festivalen og byrået begge er i Tvitvi og
 * budet er godtatt, er avtalearket avtalen: honoraret og vilkårene på programposten leses derfra, og
 * en endring fra én av partene blir en ny versjon som den andre ser med en gang. `state` sier hva som
 * gjelder nå: `open` endres direkte, `signing` venter på en kontrakt ute til signering, `signed` er låst
 * av en gjeldende kontrakt (endres med en ny kontraktversjon). Null uten delt avtaleark: da fører
 * programposten sitt eget honorar.
 */
export const performanceAgreementSchema = z
  .object({
    dealSheetId: z.uuid(),
    versionId: z.uuid(),
    versionNumber: z.number().int(),
    ourSide: z.enum(['owner', 'counterparty']),
    state: z.enum(['open', 'signing', 'signed']),
    contractId: z.uuid().nullable(),
    contractNumber: z.string().nullable(),
    counterpartyName: z.string().nullable(),
    canAmend: z.boolean(),
  })
  .meta({ id: 'PerformanceAgreement' });
export type PerformanceAgreement = z.infer<typeof performanceAgreementSchema>;

export const performanceContractDtoSchema = z
  .object({
    performanceId: z.uuid(),

    feeMinor: z.number().int().nullable(),
    feeCurrency: z.string().nullable(),
    feeKind: z.string().nullable(),
    /**
     * QA16 funn 12: the rest of the deal behind `feeKind` (the share of a versus or a door split, net or
     * gross, how guarantee and share meet), so the Avtale tab reads the whole deal and not «30 000 kr
     * Versus». Null when the contract has only an amount.
     */
    feeTerms: z
      .object({
        artistPercent: z.number().nullable(),
        versusMode: z.enum(['higher_of', 'guarantee_plus']).nullable(),
        versusBasis: z.enum(['net', 'gross']).nullable(),
        afterCosts: z.boolean().nullable(),
      })
      .nullable()
      .default(null),

    exclusivityRadiusKm: z.number().int().nullable(),
    exclusivityFrom: z.iso.date().nullable(),
    exclusivityTo: z.iso.date().nullable(),

    /** Null is «ikke avtalt», which is NOT zero — see `assertGuestSeatsAvailable`. */
    guestListSeats: z.number().int().nullable(),
    setLengthMinutes: z.number().int().nullable(),

    promoterCovers: z.string().nullable(),
    merchSplit: z.string().nullable(),
    cancellationTerms: z.string().nullable(),
    recordingTerms: z.string().nullable(),
    billing: z.string().nullable(),

    invoiceStatus: invoiceStatusSchema.nullable(),
    invoiceReference: z.string().nullable(),
    notes: z.string().nullable(),

    updatedAt: wireInstant.nullable(),

    /** Avtalen fra budet — se `contractDealSchema`. */
    deal: contractDealSchema,

    terms: z.array(contractTermDtoSchema),
    documents: z.array(contractDocumentDtoSchema),
    economy: contractEconomySchema,
    /** How many seats the guest list has already used, so the cap can be drawn as a fraction. */
    guestSeatsUsed: z.number().int(),
    /**
     * LÅST VED SIGNERING (sak 112.2, migrering 0152).
     *
     * Atle, 11. september: «når den er signert må det være låst, men med mulighet for å åpne.»
     * Vilkårene er toveis fram til noen signerer; etterpå er en endring en bevisst gjenåpning.
     *
     * Håndhevingen er en trigger i basen og ikke denne verdien — skjermen bruker den til å vise
     * låsen og tilby å åpne, framfor å la noen skrive inn i et avslag.
     */
    termsLockedAt: wireInstant.nullable(),
    termsLockedByName: z.string().nullable(),
    /** Den delte avtalen honoraret og vilkårene over er lest fra, eller null (A8). */
    agreement: performanceAgreementSchema.nullish(),
  })
  .meta({ id: 'PerformanceContract' });
export type PerformanceContractDto = z.infer<typeof performanceContractDtoSchema>;

// ------------------------------------------------------------------------------------------- //
// Requests
// ------------------------------------------------------------------------------------------- //

/**
 * Feltene i skjemaet som ER avtalen: honoraret og vilkårene. Fakturastatus, fakturareferanse og
 * notatet står utenfor, fordi de er arrangørens egen føring — et forslag fra byrået (222.4) bærer
 * dem ikke.
 */
const contractAgreementFields = {
  feeMinor: z.number().int().nullable(),
  feeCurrency: currencyCodeSchema.nullable(),
  feeKind: shortText.nullable(),

  exclusivityRadiusKm: z.number().int().min(0).max(20_000).nullable(),
  exclusivityFrom: z.iso.date().nullable(),
  exclusivityTo: z.iso.date().nullable(),

  guestListSeats: z.number().int().min(0).max(MAX_CONTRACT_SEATS).nullable(),
  setLengthMinutes: z.number().int().min(0).max(1440).nullable(),

  promoterCovers: longText.nullable(),
  merchSplit: shortText.nullable(),
  cancellationTerms: longText.nullable(),
  recordingTerms: longText.nullable(),
  billing: shortText.nullable(),

  /**
   * Avtalen, valgfri i den samme forespørselen.
   *
   * ÉN RUTE OG IKKE TO. Fanen lagres i én bevegelse, og to ruter ville betydd at halve skjemaet
   * kunne lykkes mens den andre halvparten feilet — en tilstand ingen skjerm kan tegne ærlig.
   * Utelates den, står avtalen som den var: det er forskjellen på «jeg endret ikke dette» og
   * «jeg tømte dette», og de ti feltene over har alt den andre betydningen.
   */
  deal: updateContractDealSchema.optional(),
};

/**
 * The two order checks the database also makes, so the browser says it first and the database
 * says it last. A fee with no currency is a number nobody can pay.
 */
const exclusivityInOrder = (body: {
  exclusivityFrom: string | null;
  exclusivityTo: string | null;
}): boolean =>
  body.exclusivityFrom === null ||
  body.exclusivityTo === null ||
  body.exclusivityTo >= body.exclusivityFrom;
const feeHasCurrency = (body: { feeMinor: number | null; feeCurrency: string | null }): boolean =>
  (body.feeMinor === null) === (body.feeCurrency === null);

export const updatePerformanceContractRequestSchema = z
  .object({
    ...contractAgreementFields,
    invoiceStatus: invoiceStatusSchema.nullable(),
    invoiceReference: shortText.nullable(),
    notes: longText.nullable(),
  })
  .refine(exclusivityInOrder, { error: 'exclusivity_order', path: ['exclusivityTo'] })
  .refine(feeHasCurrency, { error: 'fee_shape', path: ['feeCurrency'] })
  .meta({ id: 'UpdatePerformanceContractRequest' });
export type UpdatePerformanceContractRequest = z.infer<
  typeof updatePerformanceContractRequestSchema
>;

/**
 * HONORARET OG VILKÅRENE SLIK BYRÅET FORESLÅR DEM (sak 222.4, 0444).
 *
 * Atle, 29. september: «byrået kan bare FORESLÅ endringer i honorar og vilkår. Arrangøren godtar
 * eller avslår.» Formen er kontraktskjemaets uten fakturafeltene og notatet: et forslag er hele
 * avtalen slik byrået vil ha den, så arrangøren ser hva et ja betyr — «nå → foreslått» — også for
 * felt noen har endret siden forslaget ble sendt.
 */
export const proposedContractSchema = z
  .object(contractAgreementFields)
  .refine(exclusivityInOrder, { error: 'exclusivity_order', path: ['exclusivityTo'] })
  .refine(feeHasCurrency, { error: 'fee_shape', path: ['feeCurrency'] })
  .meta({ id: 'ProposedContract' });
export type ProposedContract = z.infer<typeof proposedContractSchema>;

const proposedTermSchema = z.object({ label: shortText, value: longText.nullable() });

export const CONTRACT_PROPOSAL_STATUSES = ['pending', 'accepted', 'declined', 'withdrawn'] as const;
export type ContractProposalStatus = (typeof CONTRACT_PROPOSAL_STATUSES)[number];
export const contractProposalStatusSchema = z
  .enum(CONTRACT_PROPOSAL_STATUSES)
  .meta({ id: 'ContractProposalStatus' });

/** Kontrakten, tilleggsvilkårene, eller begge. Et forslag uten noen av dem er ikke et forslag. */
export const createContractProposalRequestSchema = z
  .object({
    contract: proposedContractSchema.optional(),
    terms: z.array(proposedTermSchema).max(MAX_CONTRACT_TERMS).optional(),
    note: longText.nullish(),
  })
  .refine((body) => body.contract !== undefined || body.terms !== undefined, {
    error: 'proposal_empty',
    path: ['contract'],
  })
  .meta({ id: 'CreateContractProposalRequest' });
export type CreateContractProposalRequest = z.infer<typeof createContractProposalRequestSchema>;

/** Arrangørens svar, med en valgfri begrunnelse (typisk ved avslag). */
export const decideContractProposalRequestSchema = z
  .object({ note: longText.nullish() })
  .meta({ id: 'DecideContractProposalRequest' });
export type DecideContractProposalRequest = z.infer<typeof decideContractProposalRequestSchema>;

export const contractProposalDtoSchema = z
  .object({
    id: z.uuid(),
    performanceId: z.uuid(),
    /** Byrået som sendte forslaget, med navnet slik det sto da. */
    proposedByOrganizationName: z.string(),
    proposedByName: z.string().nullable(),
    /** Sant når forslaget kom fra huset som leser — da kan det trekkes tilbake, ikke avgjøres. */
    fromUs: z.boolean(),
    contract: proposedContractSchema.nullable(),
    terms: z.array(proposedTermSchema).nullable(),
    note: z.string().nullable(),
    status: contractProposalStatusSchema,
    createdAt: z.iso.datetime(),
    decidedAt: z.iso.datetime().nullable(),
    decidedByName: z.string().nullable(),
    decisionNote: z.string().nullable(),
  })
  .meta({ id: 'ContractProposal' });
export type ContractProposalDto = z.infer<typeof contractProposalDtoSchema>;

/**
 * Forslagene på én kontrakt, nyeste først. Ikke sidevist: det er én kontrakt, og lista er kappet
 * til de `MAX_CONTRACT_PROPOSALS` siste — en forhandling med femti runder leses ikke på én skjerm.
 */
export const MAX_CONTRACT_PROPOSALS = 50;
export const contractProposalListSchema = z
  .object({ items: z.array(contractProposalDtoSchema) })
  .meta({ id: 'ContractProposalList' });
export type ContractProposalList = z.infer<typeof contractProposalListSchema>;

/**
 * The extra terms, replaced whole — the shape a dinner's dishes use, for the same reason.
 *
 * A set of conditions is written as a set, and a row-at-a-time editor would be five requests to
 * record one agreement. The server diffs by LABEL, so a term that survives keeps its id.
 */
export const setContractTermsRequestSchema = z
  .object({
    terms: z
      .array(z.object({ label: shortText, value: longText.nullable() }))
      .max(MAX_CONTRACT_TERMS),
  })
  .meta({ id: 'SetContractTermsRequest' });
export type SetContractTermsRequest = z.infer<typeof setContractTermsRequestSchema>;

/**
 * A new version, naming a file that has already been uploaded.
 *
 * TWO STEPS, NOT ONE: the bytes go through `POST /v1/attachments` like every other upload — virus
 * gate, quota, checksum — and this registers what that file IS. One combined endpoint would mean a
 * second upload path with a second copy of the gate.
 */
export const addContractDocumentRequestSchema = z
  .object({
    performanceId: z.uuid(),
    attachmentId: z.uuid(),
    note: shortText.nullable(),
    signedOn: z.iso.date().nullable(),
    /** Whether this becomes the version in force. The previous one steps down in the same write. */
    makeCurrent: z.boolean(),
  })
  .meta({ id: 'AddContractDocumentRequest' });
export type AddContractDocumentRequest = z.infer<typeof addContractDocumentRequestSchema>;

/**
 * «REVIDER KONTRAKT» (215.19). En ny versjon, allerede lastet opp som vedlegg, blir den gjeldende;
 * alle tidligere versjoner som ikke alt er erstattet, merkes «Erstattet av versjon N» og kan
 * deretter bare leses. Ingen `makeCurrent`: en revisjon er alltid den som gjelder.
 */
export const reviseContractRequestSchema = z
  .object({
    attachmentId: z.uuid(),
    note: shortText.nullable(),
    signedOn: z.iso.date().nullable(),
  })
  .meta({ id: 'ReviseContractRequest' });
export type ReviseContractRequest = z.infer<typeof reviseContractRequestSchema>;

export const setCurrentDocumentRequestSchema = z
  .object({ documentId: z.uuid() })
  .meta({ id: 'SetCurrentDocumentRequest' });
export type SetCurrentDocumentRequest = z.infer<typeof setCurrentDocumentRequestSchema>;

/**
 * Whether one more guest may be added, given the contract.
 *
 * ---------------------------------------------------------------------------------------------
 * SHARED, BECAUSE THE ANSWER MUST BE THE SAME IN TWO PLACES.
 *
 * The API refuses the write and the page disables the button, and «avtalt: 10, brukt: 10» has to
 * mean the same thing to both. Written twice it eventually will not — the classic off-by-one is a
 * form that counts the row it is about to add and a server that does not.
 *
 * NULL IS NOT ZERO. A contract with no agreed seat count is a term nobody has settled, and the
 * guest list stays open; a contract that says zero is a decision, and it closes. Collapsing the
 * two would silently bar every act whose contract has not been written yet.
 */
export function guestSeatsRemaining(
  agreed: number | null,
  used: number,
): { unlimited: boolean; remaining: number } {
  if (agreed === null) return { unlimited: true, remaining: Number.POSITIVE_INFINITY };
  return { unlimited: false, remaining: Math.max(0, agreed - used) };
}

/**
 * Å ÅPNE EN SIGNERT KONTRAKT IGJEN (sak 112.2).
 *
 * En egen forespørsel og ikke et felt på `update`: å åpne er en handling man tar bevisst, og et
 * felt blant tjue andre ville blitt satt av et skjema som sendte hele raden tilbake.
 *
 * Tom kropp. Hvem og når står i revisjonsloggen, som alt annet.
 */
export const reopenContractRequestSchema = z.object({}).meta({ id: 'ReopenContractRequest' });
export type ReopenContractRequest = z.infer<typeof reopenContractRequestSchema>;
